// src/components/AppContent.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import NavbarComponent from './NavbarComponent';
import { useCallback } from 'react';
// ============================================================
// PUBLIC / AUTH PAGES
// ============================================================
import LoginPage from '../pages/auth/LoginPage';
import RegisterPage from '../pages/auth/RegisterPage';
import PinCodePage from '../pages/auth/PinCodePage';

// ============================================================
// PROVIDER ONBOARDING
// ============================================================
import CompleteProviderServicePage from '../pages/auth/CompleteProviderServicePage';
import ProfileImageProviderServicePage from '../pages/provider/ProfileImageProviderServicePage';
import ProviderGalleryPage from '../pages/provider/ProviderGalleryPage';

// ============================================================
// CLIENT PAGES
// ============================================================
import HomePage from '../pages/client/HomePage';
import ProvidersPage from '../pages/client/ProvidersPage';
import ProviderDetailPage from '../pages/client/ProviderDetailPage';
import DonationPage from '../pages/client/DonationPage';
import HelpAndSupportPage from '../pages/client/HelpAndSupportPage';
import TermsPage from '../pages/client/TermsPage';
import PrivacyPage from '../pages/client/PrivacyPage';
import AboutPage from '../pages/client/AboutPage';
import CookiePolicyPage from '../pages/client/CookiePolicyPage';

// ============================================================
// PROVIDER PAGES
// ============================================================
import ProviderDashboardPage from '../pages/provider/ProviderDashboardPage';
import ProviderServicesPage from '../pages/provider/ProviderServicesPage';
import ProviderProfilePage from '../pages/provider/ProviderProfilePage';
import ProviderSettingsPage from '../pages/provider/ProviderSettingsPage';
import ReportsPage from '../pages/provider/ReportsPage';

// ============================================================
// USER AUTH SLICE ONLY
// ============================================================
import {
  selectIsAuthenticated,
  selectAuthLoading,
  selectUser,
  selectIsProvider,
  selectIsGuestMode,
  selectIsEmailVerified,
  selectIsPhoneVerified,
  checkAuth,
  updateUser,
  clearTokens,
  logout,
} from '../store/slices/authSlice';

// ============================================================
// PIN CODE SLICE
// ============================================================
import {
  selectEmailVerified as selectPinEmailVerified,
  selectPhoneVerified as selectPinPhoneVerified,
  selectIsFullyVerified as selectPinFullyVerified,
  selectVerifiedUser,
} from '../store/slices/pinCodeSlice';

// ============================================================
// PROFILE SLICE
// ============================================================
import {
  getProfile,
  resetProfileState,
  selectProfile,
  selectProfileLoading,
  selectProfileLoaded,
} from '../store/slices/profileSlice';

// ============================================================
// COMPONENT
// ============================================================

const AppContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // ==========================================================
  // ✅ ALL HOOKS MUST BE CALLED UNCONDITIONALLY AT THE TOP LEVEL
  // ==========================================================

  // --- useState ---
  const [appReady, setAppReady] = useState(false);

  // --- useRef ---
  const authCheckDispatched = useRef(false);
  const providerRefreshDispatched = useRef(false);
  const redirectInProgress = useRef(false);
  const lastRedirectPath = useRef(null);
  const profileLoadedRef = useRef(false);
  const lastUserIdRef = useRef(null);
  const isOnProviderRoute = useRef(false);

  // --- useSelector ---
  const isUserAuthenticated = useSelector(selectIsAuthenticated);
  const authLoading = useSelector(selectAuthLoading);
  const user = useSelector(selectUser);
  const isProviderUser = useSelector(selectIsProvider);
  const isGuestMode = useSelector(selectIsGuestMode);
  const isEmailVerified = useSelector(selectIsEmailVerified);
  const isPhoneVerified = useSelector(selectIsPhoneVerified);

  const pinEmailVerified = useSelector(selectPinEmailVerified);
  const pinPhoneVerified = useSelector(selectPinPhoneVerified);
  const pinFullyVerified = useSelector(selectPinFullyVerified);
  const pinVerifiedUser = useSelector(selectVerifiedUser);

  const profile = useSelector(selectProfile);
  const profileLoading = useSelector(selectProfileLoading);
  const profileLoaded = useSelector(selectProfileLoaded);

  // ==========================================================
  // DERIVED VALUES (calculated before early return)
  // ==========================================================
  const isAuthenticated = isUserAuthenticated;
  const isProvider = isProviderUser && isUserAuthenticated;
  const userId = user?._id || user?.id || user?.userId || null;
  const currentUser = pinVerifiedUser || user;

  const finalEmailVerified =
    isEmailVerified === true ||
    pinEmailVerified === true ||
    profile?.emailVerified === true;

  const finalPhoneVerified =
    isPhoneVerified === true ||
    pinPhoneVerified === true ||
    profile?.phoneVerified === true;

  const finalIsUserVerified = (finalEmailVerified && finalPhoneVerified) || pinFullyVerified;

  // ✅ Get provider profile from Redux profile slice
  const providerProfile = profile?.providerProfile || null;

  // ==========================================================
  // ✅ PROVIDER ROUTE PATHS - For detection
  // ==========================================================
  const PROVIDER_ROUTES = useMemo(() => [
    '/provider-dashboard',
    '/provider-services',
    '/provider-profile',
    '/provider-settings',
    '/provider-reports',
    '/provider-gallery',
    '/complete-provider',
    '/profile-image-provider',
  ], []);

  const ONBOARDING_ROUTES = useMemo(() => [
    '/complete-provider',
    '/profile-image-provider',
    '/provider-gallery',
  ], []);

  // ==========================================================
  // ✅ CHECK IF CURRENT PATH IS A PROVIDER ROUTE
  // ==========================================================
  const isProviderRoute = useMemo(() => {
    return PROVIDER_ROUTES.some(route => 
      location.pathname === route || location.pathname.startsWith(`${route}/`)
    );
  }, [location.pathname, PROVIDER_ROUTES]);

  // ==========================================================
  // ✅ CHECK IF CURRENT PATH IS AN ONBOARDING ROUTE
  // ==========================================================
  const isOnboardingRoute = useMemo(() => {
    return ONBOARDING_ROUTES.some(route => 
      location.pathname === route || location.pathname.startsWith(`${route}/`)
    );
  }, [location.pathname, ONBOARDING_ROUTES]);

 const providerStatus = useMemo(() => {
  // Get provider profile from either profile slice or auth slice
  const profileProvider = profile?.providerProfile || null;
  const authProvider = user?.providerProfile || null;
  
  // Use whichever has more complete data
  const p = profileProvider || authProvider;

  // If no provider profile exists, they need to complete it
  if (!p) {
    return 'CompleteProvider';
  }

  // Check if basic info is complete
  if (!p.businessName || !p.email || !p.phone) {
    return 'CompleteProvider';
  }

  if (!p.serviceType || !p.description) {
    return 'CompleteProvider';
  }

  if (!p.address?.city || !p.location?.coordinates) {
    return 'CompleteProvider';
  }

  // Check if profile image is uploaded
  if (!p.profileImage?.url) {
    return 'ProfileImages';
  }

  // Check gallery images
  const galleryImages = p.galleryImages || p.gallery || [];
  const hasGallery = Array.isArray(galleryImages) && galleryImages.length > 0;

  // If no gallery images, redirect to gallery page
  if (!hasGallery) {
    return 'GalleryPage';
  }

  // Provider is complete - allow full access
  return 'Complete';
}, [profile, user?.providerProfile]); // Add user?.providerProfile as dependency

  const getProviderOnboardingRoute = useMemo(() => {
    const routes = {
      CompleteProvider: '/complete-provider',
      ProfileImages: '/profile-image-provider',
      GalleryPage: '/provider-gallery',
      Complete: '/provider-dashboard',
    };
    return routes[providerStatus] || '/complete-provider';
  }, [providerStatus]);

  // ==========================================================
  // ✅ useEffect - ALL MUST BE CALLED BEFORE ANY EARLY RETURN
  // ==========================================================

  // 1. INITIAL USER AUTH CHECK
  useEffect(() => {
    let mounted = true;

    const initializeUserAuth = async () => {
      try {
        if (!isUserAuthenticated && !authLoading && !authCheckDispatched.current) {
          authCheckDispatched.current = true;

          try {
            await dispatch(checkAuth()).unwrap();
          } catch {
            // No valid user session. Stay logged out.
          }
        }
      } finally {
        if (mounted) {
          setAppReady(true);
        }
      }
    };

    initializeUserAuth();

    return () => {
      mounted = false;
    };
  }, [dispatch, isUserAuthenticated, authLoading]);

  // 2. LOAD USER PROFILE
  useEffect(() => {
    if (!isUserAuthenticated || !userId) {
      if (profileLoadedRef.current) {
        profileLoadedRef.current = false;
        lastUserIdRef.current = null;
        dispatch(resetProfileState());
      }
      return;
    }

    if (profileLoaded) {
      profileLoadedRef.current = true;
      lastUserIdRef.current = userId;
      return;
    }

    if (lastUserIdRef.current === userId && profileLoadedRef.current) {
      return;
    }

    const loadProfile = async () => {
      try {
        await dispatch(getProfile()).unwrap();
        profileLoadedRef.current = true;
        lastUserIdRef.current = userId;
      } catch (error) {
        console.error('Failed to load profile:', error);
        profileLoadedRef.current = false;

        if (error === 'Session expired' || error === 'User not authenticated') {
          dispatch(clearTokens());
          dispatch(logout());
        }
      }
    };

    loadProfile();
  }, [isUserAuthenticated, userId, profileLoaded, dispatch]);

  // 3. SYNC PIN VERIFICATION
  useEffect(() => {
    if (!isUserAuthenticated || !pinFullyVerified) {
      return;
    }

    const emailVerified = pinEmailVerified === true;
    const phoneVerified = pinPhoneVerified === true;

    if (isEmailVerified !== emailVerified || isPhoneVerified !== phoneVerified) {
      dispatch(updateUser({ emailVerified, phoneVerified }));
    }
  }, [pinFullyVerified, pinEmailVerified, pinPhoneVerified, isUserAuthenticated, isEmailVerified, isPhoneVerified, dispatch]);

  // 4. PROVIDER AUTH REFRESH
  useEffect(() => {
    if (!appReady) return;
    if (!isUserAuthenticated) return;
    if (!isProvider) return;
    if (providerRefreshDispatched.current) return;

    const isProviderPath = PROVIDER_ROUTES.some(
      (path) => location.pathname === path || location.pathname.startsWith(`${path}/`)
    );

    if (!isProviderPath) return;

    providerRefreshDispatched.current = true;

    dispatch(checkAuth())
      .unwrap()
      .catch(() => {
        // Existing auth state remains available.
      });
  }, [appReady, isUserAuthenticated, isProvider, location.pathname, PROVIDER_ROUTES, dispatch]);

  // 5. RESET PROVIDER REFRESH FLAG AFTER LOGOUT
  useEffect(() => {
    if (!isUserAuthenticated) {
      providerRefreshDispatched.current = false;
    }
  }, [isUserAuthenticated]);

  // 6. REFRESH PROVIDER DATA AFTER ONBOARDING STEPS
  useEffect(() => {
    if (!isUserAuthenticated || !isProvider) return;
    if (!isOnboardingRoute && !isProviderRoute) return;
    
    const shouldRefresh = ONBOARDING_ROUTES.some(route => 
      location.pathname === route || location.pathname.startsWith(`${route}/`)
    );
    
    if (!shouldRefresh) return;
    
    const refreshProviderData = async () => {
      try {
        await dispatch(getProfile()).unwrap();
        console.log('✅ Provider data refreshed after onboarding step');
      } catch (error) {
        console.error('❌ Failed to refresh provider data:', error);
      }
    };
    
    if (userId && profileLoaded) {
      refreshProviderData();
    }
  }, [location.pathname, isUserAuthenticated, isProvider, userId, profileLoaded, dispatch, ONBOARDING_ROUTES, isOnboardingRoute, isProviderRoute]);

  // 7. SAFE NAVIGATION (this is a function, not a hook - it's fine)
  const safeNavigate = useCallback((targetPath, state = undefined) => {
    if (!targetPath) return;
    if (location.pathname === targetPath) return;
    if (redirectInProgress.current && lastRedirectPath.current === targetPath) return;

    redirectInProgress.current = true;
    lastRedirectPath.current = targetPath;

    navigate(targetPath, {
      replace: true,
      ...(state !== undefined ? { state } : {}),
    });

    setTimeout(() => {
      redirectInProgress.current = false;
    }, 250);
  }, [location.pathname, navigate]);

  // ==========================================================
  // ✅ CENTRAL PROVIDER / USER REDIRECT
  // ==========================================================
  useEffect(() => {
    // Don't run if app isn't ready or auth is loading
    if (!appReady) return;
    if (authLoading) return;
    if (redirectInProgress.current) return;

    const pathname = location.pathname;

    // ==========================================================
    // CASE 1: NOT AUTHENTICATED / GUEST
    // ==========================================================
    if (!isUserAuthenticated || isGuestMode) {
      // Allow access to public routes
      const publicRoutes = ['/home', '/providers', '/provider-details', '/donation', '/help-support', '/about', '/terms', '/privacy', '/cookies'];
      const isPublicRoute = publicRoutes.some(route => pathname === route || pathname.startsWith(`${route}/`));
      
      if (!isPublicRoute && !pathname.startsWith('/login') && !pathname.startsWith('/register')) {
        safeNavigate('/home');
      }
      return;
    }

    // ==========================================================
    // CASE 2: NOT VERIFIED - Go to PIN Code
    // ==========================================================
    if (!finalIsUserVerified && pathname !== '/pin-code') {
      safeNavigate('/pin-code', {
        email: currentUser?.email || '',
        phone: currentUser?.phone || '',
        userId: currentUser?._id || currentUser?.id || '',
        fromRegister: false,
        userData: currentUser || {},
      });
      return;
    }

    // ==========================================================
    // CASE 3: On PIN Code page - redirect appropriately
    // ==========================================================
    if (pathname === '/pin-code') {
      if (isProvider) {
        safeNavigate(getProviderOnboardingRoute);
      } else {
        safeNavigate('/home');
      }
      return;
    }

    // ==========================================================
    // CASE 4: NOT A PROVIDER - Stay on client pages
    // ==========================================================
    if (!isProvider) {
      if (isProviderRoute) {
        safeNavigate('/home');
      }
      return;
    }

    // ==========================================================
    // ✅ CASE 5: PROVIDER - Allow navigation based on status
    // ==========================================================
    
    // If provider is complete, allow all provider routes
    if (providerStatus === 'Complete') {
      return;
    }

    // If provider is incomplete, redirect to the correct onboarding route
    const requiredRoute = getProviderOnboardingRoute;
    
    // ✅ FIXED: Check if they're on the correct route
    // If they're on the required onboarding route, let them stay
    // Only redirect if they're on a different provider route
    if (pathname !== requiredRoute) {
      // Check if this is a provider route (not a client route)
      const isProviderPath = PROVIDER_ROUTES.some(
        (path) => pathname === path || pathname.startsWith(`${path}/`)
      );
      
      // Only redirect if they're trying to access a provider route
      // that's not the required onboarding route
      if (isProviderPath && !isOnboardingRoute) {
        safeNavigate(requiredRoute);
      }
    }

  }, [
    appReady,
    authLoading,
    isUserAuthenticated,
    isGuestMode,
    isProvider,
    finalIsUserVerified,
    providerStatus,
    currentUser,
    location.pathname,
    getProviderOnboardingRoute,
    safeNavigate,
    isProviderRoute,
    isOnboardingRoute,
    PROVIDER_ROUTES,
  ]);

  // ==========================================================
  // ⚠️ EARLY RETURN - AFTER ALL HOOKS ARE CALLED
  // ==========================================================
  // If we're on an admin route, don't render AppContent
  if (location.pathname.startsWith('/admin')) {
    console.log('🔴 AppContent: Admin route detected, returning null');
    return null;
  }

  // ==========================================================
  // LOADING
  // ==========================================================
  if (!appReady) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="spinner" />
          <p>Initializing application...</p>
        </div>
      </div>
    );
  }

  if (authLoading && !isAuthenticated) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="spinner" />
          <p>Loading your account...</p>
        </div>
      </div>
    );
  }

  // ==========================================================
  // ✅ PROVIDER ROUTE GUARD - SIMPLIFIED
  // ==========================================================
  const ProviderRoute = ({ children }) => {
    // Auth check
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }

    // Provider check
    if (!isProvider) {
      return <Navigate to="/home" replace />;
    }

    // Email/Phone verification check
    if (!finalIsUserVerified) {
      return <Navigate to="/pin-code" replace />;
    }

    // ✅ COMPLETE PROVIDER: Allow ALL provider pages!
    if (providerStatus === 'Complete') {
      return children;
    }

    // ✅ INCOMPLETE PROVIDER: Check if they're on the correct onboarding route
    const requiredRoute = getProviderOnboardingRoute;
    const pathname = location.pathname;

    // If they're on the correct onboarding route, allow access
    if (pathname === requiredRoute) {
      return children;
    }

    // If they're on a different onboarding route but it's still onboarding, allow access
    if (isOnboardingRoute) {
      return children;
    }

    // Otherwise, redirect to the required onboarding route
    return <Navigate to={requiredRoute} replace />;
  };

  // ==========================================================
  // INITIAL ROUTE
  // ==========================================================
  const getInitialRoute = () => {
    if (isGuestMode) return '/home';
    if (!isAuthenticated) return '/home';
    if (!finalIsUserVerified) return '/pin-code';
    if (!isProvider) return '/home';
    return getProviderOnboardingRoute;
  };

  // ==========================================================
  // RENDER
  // ==========================================================
  return (
    <div className="app-content">
      <NavbarComponent />
      <div className="app-main">
        <Routes>
          {/* AUTH */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/pin-code" element={<PinCodePage />} />

          {/* PROVIDER ONBOARDING */}
          <Route
            path="/complete-provider"
            element={
              <ProviderRoute>
                <CompleteProviderServicePage />
              </ProviderRoute>
            }
          />
          <Route
            path="/profile-image-provider"
            element={
              <ProviderRoute>
                <ProfileImageProviderServicePage />
              </ProviderRoute>
            }
          />
          <Route
            path="/provider-gallery"
            element={
              <ProviderRoute>
                <ProviderGalleryPage />
              </ProviderRoute>
            }
          />

          {/* CLIENT */}
          <Route path="/home" element={<HomePage />} />
          <Route path="/providers" element={<ProvidersPage />} />
          <Route path="/provider-details/:providerId" element={<ProviderDetailPage />} />
          <Route path="/donation" element={<DonationPage />} />
          <Route path="/help-support" element={<HelpAndSupportPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/cookies" element={<CookiePolicyPage />} />

          {/* PROVIDER */}
          <Route
            path="/provider-dashboard/*"
            element={
              <ProviderRoute>
                <ProviderDashboardPage />
              </ProviderRoute>
            }
          />
          <Route
            path="/provider-services"
            element={
              <ProviderRoute>
                <ProviderServicesPage />
              </ProviderRoute>
            }
          />
          <Route
            path="/provider-profile"
            element={
              <ProviderRoute>
                <ProviderProfilePage />
              </ProviderRoute>
            }
          />
          <Route
            path="/provider-settings"
            element={
              <ProviderRoute>
                <ProviderSettingsPage />
              </ProviderRoute>
            }
          />
          <Route
            path="/provider-reports"
            element={
              <ProviderRoute>
                <ReportsPage />
              </ProviderRoute>
            }
          />

          {/* DEFAULT */}
          <Route path="/" element={<Navigate to={getInitialRoute()} replace />} />
          <Route path="*" element={<Navigate to={getInitialRoute()} replace />} />
        </Routes>
      </div>
    </div>
  );
};

export default AppContent;